import { Box, Stack } from "@mui/material";
import { Control, FieldErrors, FieldValues, Path, useController } from "react-hook-form";

import { ChoiceFormData } from "../types";

import { getAlreadySelectedDisplay } from "@/common/helpers/customAnswerChoices.helper";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTextField } from "@/dashboard-design-system/TextField";
import { Delete } from "@/icons/outlined";

interface ChoiceRowV4Props<T extends FieldValues> {
  choice: ChoiceFormData;
  index: number;
  control: Control<T>;
  name: Path<T>;
  field: ReturnType<typeof useController<T>>["field"];
  errors?: FieldErrors;
  isRequired: boolean;
  showMaximumField: boolean;
  showDelete: boolean;
  answerCounts: Record<string, number> | undefined;
  onDelete: () => void;
}

export function ChoiceRowV4<T extends FieldValues>({
  choice,
  index,
  control,
  name,
  field,
  errors,
  isRequired,
  showMaximumField,
  showDelete,
  answerCounts,
  onDelete,
}: ChoiceRowV4Props<T>) {
  const { t } = useTranslate();

  const choiceErrors = (errors?.choices as FieldErrors<FieldValues>[] | undefined)?.[index]?.body as
    | FieldErrors<FieldValues>
    | undefined;
  const bodyName = `${name}.${index}.body` as Path<T>;
  const maximumName = `${name}.${index}.maximum` as Path<T>;
  const idName = `${name}.${index}.id` as Path<T>;
  const sortIndexName = `${name}.${index}.sortIndex` as Path<T>;
  const valueErrorMessage = choiceErrors?.message as string | undefined;

  return (
    <Box>
      {choice.id && <input type="hidden" {...field} name={idName} value={choice.id} />}
      <input type="hidden" {...field} name={sortIndexName} value={choice.sortIndex} />
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ flex: 1 }}>
          <DashboardTextField
            control={control}
            name={bodyName}
            label={t("dashboard", "donationForm.choiceProposition", { number: index + 1 })}
            required={isRequired}
            error={Boolean(valueErrorMessage)}
            errorMessage={valueErrorMessage || undefined}
            rules={[{ required: { value: isRequired, message: t("common", "isRequired") } }]}
          />
        </Box>

        {showMaximumField && (
          <Box sx={{ width: 140 }}>
            <DashboardTextField
              type="number"
              control={control}
              name={maximumName}
              label={t("dashboard", "additionalQuestion.limit")}
              helperText={t("dashboard", "additionalQuestion.limitAlreadySelected", {
                count: getAlreadySelectedDisplay(choice, answerCounts),
              })}
            />
          </Box>
        )}

        {showDelete && (
          <Box sx={{ pt: 3.5 }}>
            <DashboardIconButton onClick={onDelete} size="small" vibe="neutral">
              <Delete />
            </DashboardIconButton>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
